import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material/styles";
import { useLoading } from "context/LoadingContext";
import { useTranslation } from "hooks/useTranslation";

const spinReverse = keyframes`
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.2; transform: scale(0.85); }
  40% { opacity: 1; transform: scale(1); }
`;

const cardPop = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

export default function GlobalLoader() {
  const { isLoading } = useLoading();
  const { t } = useTranslation();
  return (
    <Backdrop
      open={isLoading}
      transitionDuration={{ enter: 200, exit: 200 }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 10,
        backgroundColor: "rgba(15, 23, 42, 0.45)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          px: 4,
          py: 3,
          borderRadius: 3,
          bgcolor: "rgba(255, 255, 255, 0.92)",
          boxShadow: "0 18px 50px rgba(15, 23, 42, 0.25)",
          animation: `${cardPop} 220ms ease-out`,
        }}
      >
        <Box sx={{ position: "relative", width: 64, height: 64 }}>
          {/* outer ring */}
          <CircularProgress
            size={64}
            thickness={4}
            sx={{
              color: "#1976d2",
              position: "absolute",
              inset: 0,
            }}
          />
          {/* inner counter-rotating ring */}
          <CircularProgress
            size={44}
            thickness={5}
            variant="indeterminate"
            sx={{
              color: "#64b5f6",
              position: "absolute",
              top: 10,
              left: 10,
              animation: `${spinReverse} 1.4s linear infinite`,
              "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#0f172a",
              letterSpacing: 0.3,
              fontSize: "0.95rem",
            }}
          >
            {t("common.loading", "Loading")}
          </Typography>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                bgcolor: "#1976d2",
                animation: `${dotPulse} 1.2s ease-in-out infinite`,
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
        </Box>
      </Box>
    </Backdrop>
  );
}
